import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { meApi, taskApi } from '../services/api';
import { conversationsApi, Conversation } from '../services/conversationsApi';
import { CallWindow, Lead, Task, TodayStats } from '../types';
import { IST, STAGE_LABEL, callWindowOf, fmtTime, istDay, istToday, label, shiftDay } from '../lib';
import { Failed, Loading, StageChip, WorkRow } from '../components/WorkBits';
import { purposeOf, visitTone, leadName, TaskActions, useCallModal, useChatSheet, isCheckingCall, isOverdue } from './queueBits';
import { useCallWindows } from '../context/CallWindowsContext';
import { useAuth } from '../context/AuthContext';
import { ScheduleSheet } from '../components/lead/Work';
import SiteVisitModal from '../components/SiteVisitModal';
import { interestOf } from '../components/lead/leadModel';

/** The operator's day — the Nurture app's Today tab at desktop width.
 *
 *  Everything here is the signed-in operator's own: their calling stats, their
 *  AI suggestions, their queue. The queue is one row per lead (its most urgent
 *  open task): today grouped by the calling windows, then the overdue backlog
 *  by how far back it goes; work due on later days drops off and lives in
 *  Queue. Visits and replies sit in the right rail. */

const URGENCY = (t: Task) => new Date(t.due_at).getTime();

/** One row per lead — the same pick as the Nurture app's `mostUrgent`. Anything
 *  due today wins: the earliest already past, else the earliest still to come.
 *  With nothing due today it is the MOST RECENT overdue task, so a lead never
 *  sits in Older when it also has something from this week. A lead whose open
 *  work is all on later days drops off. Site visits have their own card. */
const perLead = (tasks: Task[]): Task[] => {
  const today = istToday();
  const now = Date.now();
  const byLead = new Map<string, Task[]>();
  tasks.filter((t) => t.type !== 'site_visit').forEach((t) => {
    byLead.set(t.lead_id, [...(byLead.get(t.lead_id) || []), t]);
  });
  const rows: Task[] = [];
  byLead.forEach((mine) => {
    const asc = [...mine].sort((a, b) => URGENCY(a) - URGENCY(b));
    const todays = asc.filter((t) => istDay(t.due_at) === today);
    const pick = todays.find((t) => URGENCY(t) < now) ?? todays[0]
      ?? asc.filter((t) => istDay(t.due_at) < today).pop();
    if (pick) rows.push(pick);
  });
  return rows;
};

/** Past its time: its window has closed, or it was due on an earlier day. */
const isBehind = (t: Task, windows: CallWindow[], today: string) =>
  isOverdue(t, windows) || istDay(t.due_at) < today;

/** The deep backlog opens folded away: it is there to reach, not to scroll past. */
const COLLAPSED_AT_START = ['this-month', 'older'];

/** Today first — Earlier today (behind) → one group per calling window → Later —
 *  then the overdue backlog from earlier days in the nearest bucket that holds
 *  it: Yesterday, This week (Monday–Sunday, before yesterday), This month, Older.
 *  Windows and Later run in clock order; the overdue groups run most recent
 *  first. Days after today never reach here. */
const groupByWindow = (rows: Task[], windows: CallWindow[]) => {
  const today = istToday();
  const yesterday = shiftDay(today, -1);
  const weekStart = shiftDay(today, -((new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7));
  const monthStart = `${today.slice(0, 8)}01`;
  const keyOf = (t: Task): string => {
    const day = istDay(t.due_at);
    if (day === yesterday) return 'yesterday';
    if (day < today) return day >= weekStart ? 'this-week' : day >= monthStart ? 'this-month' : 'older';
    if (isBehind(t, windows, today)) return 'earlier-today';
    return callWindowOf(t.due_at, windows)?.start ?? 'later';
  };
  const asc = [...rows].sort((a, b) => URGENCY(a) - URGENCY(b));
  const group = (key: string, title: string, newestFirst = false) => {
    const mine = asc.filter((t) => keyOf(t) === key);
    return { key, title, rows: newestFirst ? mine.reverse() : mine };
  };
  return [
    group('earlier-today', 'Earlier today', true),
    ...windows.map((w) => group(w.start, `${label(w.name)} · ${w.start}–${w.end}`)),
    group('later', 'Later'),
    group('yesterday', 'Yesterday', true),
    group('this-week', 'This week', true),
    group('this-month', 'This month', true),
    group('older', 'Older', true),
  ].filter((g) => g.rows.length > 0);
};

const greeting = () => {
  const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: 'numeric', hourCycle: 'h23' }).format(new Date()));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const talkTime = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m` : `${Math.round(s / 60)}m`);

const TEMP: Record<string, string> = { high: 'bg-brick', medium: 'bg-gold', low: 'bg-sky-wash' };

const TodayPage: React.FC = () => {
  const navigate = useNavigate();
  const { call, modal } = useCallModal();
  const { chat, chatLead, sheet } = useChatSheet();
  const windows = useCallWindows();
  const [stats, setStats] = useState<TodayStats | null>(null);
  const [mine, setMine] = useState<Task[] | null>(null);
  const [proposed, setProposed] = useState<Task[] | null>(null);
  const [chats, setChats] = useState<Conversation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>('all');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(COLLAPSED_AT_START));
  const [review, setReview] = useState<Task | null>(null);

  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 30 * 86400000);
      // Today shows what is due today plus the overdue backlog, never the
      // future — so ask core for exactly that window. On "Everyone" the
      // unbounded list is the whole book (3,900 tasks, 11 MB, 12 s).
      const dueBefore = new Date(`${shiftDay(istToday(), 1)}T00:00:00+05:30`).toISOString();
      const [s, m, p, c] = await Promise.all([
        meApi.todayStats(),
        taskApi.list({ status: 'open', due_before: dueBefore }),
        taskApi.list({ status: 'proposed' }),
        conversationsApi.getConversations({ from, to }, 30, 0),
      ]);
      setStats(s);
      setMine(m.tasks);
      setProposed(p.tasks);
      setChats(c.conversations);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (mine ? perLead(mine) : []), [mine]);
  const stageCounts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((t) => { const s = t.lead?.stage || 'unknown'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [rows]);

  if (error) return <div className="px-7 py-6"><Failed what="today" error={error} /></div>;
  if (!stats || !mine || !proposed || !chats) {
    return <div className="px-7 py-6"><Loading what="today" /></div>;
  }

  const today = istToday();
  const shown = stage === 'all' ? rows : rows.filter((t) => (t.lead?.stage || 'unknown') === stage);
  const groups = groupByWindow(shown, windows);
  const behind = rows.filter((t) => isBehind(t, windows, today)).length;
  const visitsToday = mine.filter((t) => t.type === 'site_visit' && istDay(t.due_at) === today);
  const unread = chats.filter((c) => c.unread_count > 0);
  const firstName = (user?.name || '').split(' ')[0];

  const openLead = (t: Task) => navigate(`/leads/${t.lead_id}`);
  const toggle = (k: string) => setCollapsed((s) => {
    const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  const STAT = (v: React.ReactNode, k: string, head = false) => (
    <div className="px-3 first:pl-0 border-l-[1.5px] border-dotted border-sketch first:border-l-0">
      <div className={`font-display text-[24px] font-semibold num leading-tight ${head ? 'text-forest' : ''}`}>{v}</div>
      <div className="text-[11.5px] text-graphite mt-0.5">{k}</div>
    </div>
  );

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting()}{firstName ? `, ${firstName}` : ''}</h1>
          <p className="page-sub num">
            {new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short' }).format(new Date())}
            {' · '}<b>{behind}</b> behind · <b>{rows.length - behind}</b> ahead today · all times IST
          </p>
        </div>
        <button className="btn" onClick={load}>Refresh</button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_330px] gap-3.5 items-start">
        <div className="space-y-3.5 min-w-0">
          <div className="card">
            <h3 className="card-h">Today's calling</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-y-3">
              {STAT(stats.dials, 'Dials')}
              {STAT(<>{stats.connected}{stats.dials > 0 && <small className="text-[13px] text-graphite ml-1">{Math.round((stats.connected / stats.dials) * 100)}%</small>}</>, 'Connected', true)}
              {STAT(talkTime(stats.talk_seconds), 'Talk time')}
              {STAT(stats.conversations, 'Real conversations')}
              {STAT(stats.leads_reached, 'Leads reached')}
            </div>
          </div>

          {proposed.length > 0 && (
            <div className="card border-amber-ink/30">
              <h3 className="card-h !text-amber-ink">Suggested for you · {proposed.length}</h3>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3">
                {proposed.map((t) => (
                  <div key={t.id} className="border-[1.5px] border-amber-ink/30 bg-gold/[0.07] rounded-lg px-3.5 py-3 flex flex-col gap-2 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <b className="text-[13.5px]">{leadName(t)}</b>
                      <span className="chip chip-gold">✦ {t.type === 'site_visit' ? 'Site visit' : label(t.type)}</span>
                      <span className="chip chip-mute num">{istDay(t.due_at) === today ? fmtTime(t.due_at) : new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(t.due_at))}</span>
                    </div>
                    {purposeOf(t) && <p className="text-[12.5px] leading-snug m-0">{purposeOf(t)}</p>}
                    <div className="flex gap-1.5 mt-auto">
                      <button className="btn btn-pri btn-sm" disabled={!t.lead} onClick={() => setReview(t)}>Review</button>
                      <button className="btn btn-sm" onClick={async () => { await taskApi.reject(t.id, 'rejected in portal'); load(); }}>Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="card-h">Your queue · {rows.length} {rows.length === 1 ? 'lead' : 'leads'}</h3>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {[['all', rows.length] as const, ...Object.entries(stageCounts)].map(([k, n]) => (
                <button key={k} className="pill whitespace-nowrap" aria-pressed={stage === k} onClick={() => setStage(k)}>
                  {k === 'all' ? 'All' : STAGE_LABEL[k] ?? label(k)} <span className="num opacity-70 ml-0.5">{n}</span>
                </button>
              ))}
            </div>
            {groups.length === 0 && <p className="text-graphite text-[13px] mt-3">Nothing left for today.</p>}
            {groups.map((g) => (
              <div key={g.key}>
                <button className="groupline w-full text-left" onClick={() => toggle(g.key)}>
                  {collapsed.has(g.key) ? '▸' : '▾'} {g.title} <span className="num text-charcoal">{g.rows.length}</span>
                </button>
                {!collapsed.has(g.key) && g.rows.map((t) => {
                  const lead = t.lead as Lead | undefined;
                  const temp = lead ? interestOf(lead) : null;
                  const late = isBehind(t, windows, today);
                  return (
                    <WorkRow
                      key={t.id}
                      tone={isCheckingCall(t) ? 'forest' : late ? 'brick' : 'sky'}
                      when={isCheckingCall(t) ? 'Checking…' : istDay(t.due_at) === today ? fmtTime(t.due_at)
                        : new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short' }).format(new Date(t.due_at))}
                      whenLate={late && !isCheckingCall(t)}
                      title={leadName(t)}
                      detail={[label(t.type), purposeOf(t)].filter(Boolean).join(' · ')}
                      onClick={() => openLead(t)}
                      right={
                        <span className="flex items-center gap-2.5">
                          {temp && <span className={`w-2 h-2 rounded-full ${TEMP[temp] || 'bg-sage'}`} title={`${temp} interest`} />}
                          <StageChip stage={lead?.stage ?? null} />
                          <TaskActions task={t} onCall={() => call(t.lead)} onChat={() => chatLead(t.lead)} />
                        </span>
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3.5 min-w-0">
          <div className="card">
            <h3 className="card-h">Visits today · {visitsToday.length}</h3>
            {visitsToday.length === 0
              ? <p className="text-graphite text-[13px]">No visits booked for today.</p>
              : visitsToday.map((t) => (
                <WorkRow key={t.id} tone={visitTone(t)} when={fmtTime(t.due_at)} title={leadName(t)}
                  detail={t.payload?.reconfirmed_at ? 'Confirmed' : 'Not confirmed yet'} onClick={() => openLead(t)} />
              ))}
            <button className="btn mt-3" onClick={() => navigate('/visits')}>Open the visit board →</button>
          </div>

          <div className="card">
            <h3 className="card-h">Replies waiting · {unread.length}</h3>
            {unread.length === 0
              ? <p className="text-graphite text-[13px]">Every reply has been answered.</p>
              : unread.slice(0, 6).map((c) => (
                <WorkRow
                  key={c.id}
                  tone="sky"
                  title={c.recipient_name || c.title || 'Unknown'}
                  detail={c.last_message}
                  onClick={() => chat({ conversationId: c.id, title: c.recipient_name || c.title || 'Chat', subtitle: c.recipient_id })}
                  right={<span className="chip chip-sky num">{c.unread_count}</span>}
                />
              ))}
            <button className="btn mt-3" onClick={() => navigate('/messages')}>Open messages →</button>
          </div>
        </div>
      </div>

      {review && review.lead && (review.type === 'site_visit' ? (
        <SiteVisitModal lead={review.lead} visitTask={null} proposal={review}
          onClose={() => setReview(null)} onChanged={load} />
      ) : (
        <ScheduleSheet lead={review.lead} proposal={review} onClose={() => setReview(null)} onChanged={load} />
      ))}
      {modal}
      {sheet}
    </div>
  );
};

export default TodayPage;
